import type { WorkRow } from './workRows';

/**
 * What a Working block's line says, as data. The line names the work while it
 * is live and summarises it once settled; elapsed time is the fallback when
 * there is nothing better to say. Wording lives in the UI (`formatWorkingLine`).
 */
export type SettledSummary =
  | { form: 'yield'; count: number }
  | { form: 'steps'; count: number }
  | { form: 'row'; row: WorkRow }
  | { form: 'duration'; seconds: number | null };

export type LineState =
  | { kind: 'working'; seconds: number | null }
  | { kind: 'phrases'; phrases: readonly string[] }
  | { kind: 'row'; row: WorkRow; tense: 'active' | 'past' }
  | { kind: 'settled'; summary: SettledSummary };

/**
 * Where the duration format gains a minutes component (`58s` → `1m 2s`). At or
 * past it, a settled block of several rows reports how many rather than how long.
 */
export const SLOW_BLOCK_SECONDS = 60;

/** The line reads whole seconds, so a settled span under one is no reading at all. */
const MIN_MEASURED_SECONDS = 1;

export interface LineStateInput {
  rows: readonly WorkRow[];
  live: boolean;
  /** The clinician has opened the block; the rows below then name the work. */
  open: boolean;
  seconds: number | null;
  /** Sources the block's only row returned, when it is an Evidence call. */
  yieldCount: number | null;
  /** The turn's contextual loader phrases; empty when the backend sent none. */
  phrases: readonly string[];
}

const isTerminal = (row: WorkRow): boolean =>
  row.phase === 'succeeded' || row.phase === 'failed' || row.phase === 'cancelled';

/**
 * The row with the greatest stamp; timeline order breaks ties and stands in
 * when stamps are missing, so the choice is deterministic either way.
 */
const latestBy = (
  rows: readonly WorkRow[],
  stampOf: (row: WorkRow) => number | undefined
): WorkRow | undefined => {
  let latest: WorkRow | undefined;
  let latestStamp = Number.NEGATIVE_INFINITY;
  for (const row of rows) {
    const stamp = stampOf(row) ?? Number.NEGATIVE_INFINITY;
    if (stamp >= latestStamp) {
      latest = row;
      latestStamp = stamp;
    }
  }
  return latest;
};

const deriveLiveLine = (
  rows: readonly WorkRow[],
  seconds: number | null,
  phrases: readonly string[]
): LineState => {
  const running = latestBy(
    rows.filter((row) => row.phase === 'running' && row.named),
    (row) => row.seenAtMs?.first
  );
  if (running) return { kind: 'row', row: running, tense: 'active' };

  // A finished row with no completed-tense label would read as still running.
  const finished = latestBy(rows.filter(isTerminal), (row) => row.seenAtMs?.last);
  if (finished?.hasPastTense) return { kind: 'row', row: finished, tense: 'past' };

  // Nothing to name: the turn's own phrases say more than a clock.
  if (phrases.length > 0) return { kind: 'phrases', phrases };
  return { kind: 'working', seconds };
};

const deriveSettledSummary = (
  rows: readonly WorkRow[],
  seconds: number | null,
  yieldCount: number | null
): SettledSummary => {
  const [only] = rows;
  // Only work that succeeded yielded anything; a failed or cancelled step says so.
  if (
    rows.length === 1 &&
    only?.phase === 'succeeded' &&
    yieldCount !== null &&
    yieldCount > 0
  ) {
    return { form: 'yield', count: yieldCount };
  }
  // One finished row with a completed-tense label says what was done, however
  // fast it ran. A row the block settled on mid-flight has no past to name.
  if (rows.length === 1 && only && isTerminal(only) && only.hasPastTense) {
    return { form: 'row', row: only };
  }
  // Under a second is below the line's resolution: a burst of frames, or a block
  // closed at an approval gate, measures ~0 however long it was watched. Like an
  // unknown duration, it cannot claim the work was quick.
  const measured = seconds !== null && seconds >= MIN_MEASURED_SECONDS;
  const slow = !measured || seconds >= SLOW_BLOCK_SECONDS;
  if (slow && rows.length >= 2) return { form: 'steps', count: rows.length };
  return { form: 'duration', seconds: measured ? seconds : null };
};

export const deriveLineState = ({
  rows,
  live,
  open,
  seconds,
  yieldCount,
  phrases,
}: LineStateInput): LineState => {
  if (!live) {
    return { kind: 'settled', summary: deriveSettledSummary(rows, seconds, yieldCount) };
  }
  if (open) return { kind: 'working', seconds };
  return deriveLiveLine(rows, seconds, phrases);
};
